import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getClaimVerificationEmail, useClaimYourBusiness } from 'shared/hooks/ClaimYourBusiness';
import { usePearlModal } from 'framework';
import { GeocodeService } from 'lib/apis/GeocodeService';
import { TokenService } from 'lib/apis/TokenService';
import { redirectToVendorConnectOrFallback } from 'shared/lib/VendorConnectRedirect';
import { CONTACTS } from 'shared/constants/navigation';
import {
    CodeFieldColumn,
    CodeInput,
    CodeInputWrap,
    FieldLabel,
} from 'vendor/components/VendorBasicDetails/EmailVerificationPanel.styled';
import {
    ClaimableWebstoresDialogStyled,
    DialogBackLink,
    DialogClaimButton,
    DialogCompleteButton,
    DialogContinueLink,
    DialogCodeField,
    DialogResendLink,
    DialogSendButton,
} from './ClaimableWebstoresDialog.styled';

const DIALOG_STEPS = {
    LIST: 'list',
    SEND_CODE: 'sendCode',
    VERIFY_CODE: 'verifyCode',
    CLAIM_SUCCESS: 'claimSuccess',
};

const resolveCategoryName = (categoryList, categoryIdStr) => {
    if (!categoryIdStr) return undefined;
    const firstId = String(categoryIdStr).split(',')[0]?.trim();
    const parsed = parseInt(firstId, 10);
    if (Number.isNaN(parsed)) return undefined;
    return categoryList.find((c) => c.id === parsed)?.name;
};

const resolveSubcategoryName = (categoryList, subcategoryMap, categoryIdStr) => {
    if (!subcategoryMap || typeof subcategoryMap !== 'object') return undefined;
    const firstCategoryId = parseInt(
        String(categoryIdStr || '')
            .split(',')[0]
            ?.trim(),
        10
    );
    const category = !Number.isNaN(firstCategoryId)
        ? categoryList.find((c) => c.id === firstCategoryId)
        : undefined;
    if (!category) return undefined;
    const selectedSubIds = subcategoryMap[category.id] || [];
    const firstSubId = selectedSubIds[0];
    if (firstSubId == null) return undefined;
    return category.subCategories?.find((s) => s.id === firstSubId)?.name;
};

const buildLocationLabel = (result) => {
    const first = Array.isArray(result.addresses) ? result.addresses[0] : null;
    if (!first) return '';
    return [first.city, first.state].filter(Boolean).join(', ');
};

const buildAddressLabel = (result) => {
    let addresses = result?.addresses;
    if (typeof addresses === 'string') {
        try {
            addresses = JSON.parse(addresses);
        } catch {
            addresses = null;
        }
    }
    const first = Array.isArray(addresses) ? addresses[0] : null;
    if (!first) return '';
    const line1 =
        first.address1 || first.addressLine1 || first.street1 || first.street || first.address;
    const line2 = first.address2 || first.addressLine2 || first.street2;
    const cityState = [first.city, first.state].filter(Boolean).join(', ');
    const zip = first.zipCode || first.zip || first.postalCode;
    const street = [line1, line2].filter(Boolean).join(' ');
    const locality = [cityState, zip].filter(Boolean).join(' ');
    return [street, locality].filter(Boolean).join(', ');
};

const buildCategoryLabel = (result) => {
    const mapping = result.subcategoryMappingNames;
    if (mapping && typeof mapping === 'object') {
        const segments = Object.entries(mapping).map(([category, subs]) => {
            if (Array.isArray(subs) && subs.length > 0) {
                return `${category} \u2022 ${subs.join(', ')}`;
            }
            return category;
        });
        if (segments.length > 0) return segments.join(' / ');
    }
    if (Array.isArray(result.categoryNames) && result.categoryNames.length > 0) {
        return result.categoryNames.join(', ');
    }
    return '';
};

const extractLatLon = (result) => {
    const lat =
        result?.location?.lat ??
        result?.location?.latitude ??
        result?.latitude ??
        result?.lat ??
        (Array.isArray(result?.addresses) ? result.addresses[0]?.latitude : undefined);
    const lon =
        result?.location?.lon ??
        result?.location?.lng ??
        result?.location?.longitude ??
        result?.longitude ??
        result?.lon ??
        (Array.isArray(result?.addresses) ? result.addresses[0]?.longitude : undefined);

    const parsedLat = lat != null ? Number(lat) : NaN;
    const parsedLon = lon != null ? Number(lon) : NaN;
    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLon)) return null;
    return { lat: parsedLat, lon: parsedLon };
};

const toClaimableWebstore = (result) => ({
    Id: result.webstoreId,
    IsCYB: true,
    WebstoreName: result.name,
    Addresses: Array.isArray(result.addresses) ? result.addresses : [],
    DisplayStoreAddress: false,
});

const claimDialogHighlight = <span className="dialog-verify-email" />;

const getVerificationEmailLabel = (verificationEmail, t) =>
    verificationEmail || t('Forms.BusinessInformation.ClaimSuggestions.EmailOnFile');

const DialogSupportContact = ({ supportEmail, t }) => (
    <p className="dialog-verify-subtitle" data-testid="dialog-support-contact">
        {t('Forms.BusinessInformation.ClaimSuggestions.SupportContact', {
            email: supportEmail,
            phone: CONTACTS.PHONE_NUMBER,
        })}
    </p>
);

const ClaimableWebstoresDialogSendCodeStep = ({
    webstoreToClaim,
    verificationEmail,
    supportEmail,
    isSending,
    onBack,
    onSendCode,
}) => {
    const { t } = useTranslation();
    const emailLabel = getVerificationEmailLabel(verificationEmail, t);

    return (
        <>
            <DialogBackLink onClick={onBack} data-testid="dialog-back-to-list">
                {t('Forms.Labels.Back')}
            </DialogBackLink>
            <h2 className="dialog-heading">{t('Pages.ClaimSendCodeVerify.Title')}</h2>
            <div className="dialog-verify-body" data-testid="dialog-send-code-step">
                <Trans
                    i18nKey="Forms.BusinessInformation.ClaimSuggestions.SendCodeMessage"
                    values={{
                        storeName: webstoreToClaim?.WebstoreName,
                        email: emailLabel,
                    }}
                    components={{
                        storeName: claimDialogHighlight,
                        email: claimDialogHighlight,
                    }}
                />
                <DialogSupportContact supportEmail={supportEmail} t={t} />
            </div>
            <DialogSendButton
                onClick={onSendCode}
                disabled={isSending}
                data-testid="dialog-send-verification-code"
            >
                {t('Forms.BusinessInformation.ClaimSuggestions.SendCodeButton')}
            </DialogSendButton>
        </>
    );
};

const ClaimableWebstoresDialogVerifyCodeStep = ({
    webstoreToClaim,
    verificationEmail,
    supportEmail,
    code,
    isSubmitting,
    isResending,
    onBack,
    onCodeChange,
    onResendCode,
    onComplete,
}) => {
    const { t } = useTranslation();
    const emailLabel = getVerificationEmailLabel(verificationEmail, t);

    return (
        <>
            <DialogBackLink onClick={onBack} data-testid="dialog-back-to-list">
                {t('Forms.Labels.Back')}
            </DialogBackLink>
            <h2 className="dialog-heading">{t('Pages.ClaimVerifyYourId.Title')}</h2>
            <div className="dialog-verify-body" data-testid="dialog-verify-code-step">
                <Trans
                    i18nKey="Forms.BusinessInformation.ClaimSuggestions.VerifyCodeMessage"
                    values={{
                        storeName: webstoreToClaim?.WebstoreName,
                        email: emailLabel,
                    }}
                    components={{
                        storeName: claimDialogHighlight,
                        email: claimDialogHighlight,
                    }}
                />
            </div>
            <DialogCodeField>
                <CodeFieldColumn>
                    <FieldLabel htmlFor="claim-dialog-verification-code">
                        {t('Forms.BasicDetails.VerificationModalContent.VerificationCodeLabel')}
                    </FieldLabel>
                    <CodeInputWrap>
                        <CodeInput
                            id="claim-dialog-verification-code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder={t(
                                'Forms.BasicDetails.VerificationModalContent.VerificationCodePlaceholder'
                            )}
                            onChange={onCodeChange}
                            value={code}
                            maxLength={8}
                            data-testid="dialog-verification-code-input"
                        />
                    </CodeInputWrap>
                </CodeFieldColumn>
                <DialogResendLink
                    onClick={onResendCode}
                    disabled={isResending}
                    data-testid="dialog-resend-code"
                >
                    {t('Pages.ClaimVerifyYourId.ResendCode')}
                </DialogResendLink>
            </DialogCodeField>
            <DialogSupportContact supportEmail={supportEmail} t={t} />
            <DialogCompleteButton
                onClick={onComplete}
                disabled={isSubmitting || !code}
                data-testid="dialog-complete-claim"
            >
                {t('Pages.ClaimVerifyYourId.Continue')}
            </DialogCompleteButton>
        </>
    );
};

const ClaimableWebstoresDialogSuccessStep = ({ webstoreToClaim, onContinue, t }) => (
    <div className="dialog-success-step" data-testid="dialog-claim-success-step">
        <h2 className="dialog-heading">
            {t('Forms.BusinessInformation.ClaimSuggestions.ClaimSuccessTitle')}
        </h2>
        <p className="dialog-verify-body">
            <Trans
                i18nKey="Forms.BusinessInformation.ClaimSuggestions.ClaimSuccessMessage"
                values={{ storeName: webstoreToClaim?.WebstoreName }}
                components={{ storeName: claimDialogHighlight }}
            />
        </p>
        <p className="dialog-verify-subtitle">
            {t('Forms.BusinessInformation.ClaimSuggestions.ClaimSuccessRedirect')}
        </p>
        <DialogSendButton
            type="button"
            onClick={onContinue}
            data-testid="dialog-continue-to-connect"
        >
            {t('Forms.BusinessInformation.ClaimSuggestions.ClaimSuccessContinue')}
        </DialogSendButton>
    </div>
);

const ClaimableWebstoresDialogListStep = ({ results, onClaim, onContinue, t }) => (
    <>
        <h2 className="dialog-heading">
            {t('Forms.BusinessInformation.ClaimSuggestions.Heading')}
        </h2>
        <p className="dialog-subtitle">
            {t('Forms.BusinessInformation.ClaimSuggestions.Subtitle')}
        </p>
        <div className="dialog-list">
            {results.map((result) => {
                const location = buildLocationLabel(result);
                const address = buildAddressLabel(result);
                const distance =
                    result.distanceKm != null
                        ? t('Forms.BusinessInformation.ClaimSuggestions.DistanceLabel', {
                              distance: result.distanceKm,
                          })
                        : '';
                const categoryLabel = buildCategoryLabel(result);
                const metaText = [location, distance].filter(Boolean).join(' \u2022 ');
                const addressOrLocation = address || result?.__reverseGeocodedAddress || location;

                return (
                    <div
                        key={result.webstoreId}
                        className="dialog-card"
                        data-testid={`dialog-card-${result.webstoreId}`}
                    >
                        <div className="dialog-card-name">{result.name}</div>
                        {metaText && <div className="dialog-card-meta">{metaText}</div>}
                        {categoryLabel && (
                            <div className="dialog-card-categories">{categoryLabel}</div>
                        )}
                        {addressOrLocation && (
                            <div className="dialog-card-address">{addressOrLocation}</div>
                        )}
                        <div className="dialog-card-actions">
                            <DialogClaimButton
                                onClick={() => onClaim(result)}
                                data-testid={`dialog-claim-${result.webstoreId}`}
                            >
                                {t('Forms.BusinessInformation.ClaimSuggestions.ClaimButton')}
                            </DialogClaimButton>
                        </div>
                    </div>
                );
            })}
        </div>
        <div className="dialog-footer">
            <DialogContinueLink
                type="button"
                onClick={onContinue}
                data-testid="dialog-continue-new"
            >
                {t('Forms.BusinessInformation.ClaimSuggestions.NoneOfThese')}
            </DialogContinueLink>
        </div>
    </>
);

const ClaimableWebstoresDialog = ({ results, onClaim, onContinue }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { closeModal } = usePearlModal();
    const { setClaimableStore, sendVerificationCode, resendCode, completeClaim } =
        useClaimYourBusiness();

    const [step, setStep] = useState(DIALOG_STEPS.LIST);
    const [webstoreToClaim, setWebstoreToClaim] = useState(null);
    const [code, setCode] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reverseGeocodedAddresses, setReverseGeocodedAddresses] = useState({});

    const verificationEmail = getClaimVerificationEmail(webstoreToClaim);
    const supportEmail = CONTACTS.VENDOR_EMAIL;
    const geocodeService = useMemo(() => GeocodeService(), []);

    const hydratedResults = useMemo(
        () =>
            results.map((r) => ({
                ...r,
                __reverseGeocodedAddress: reverseGeocodedAddresses?.[r?.webstoreId],
            })),
        [results, reverseGeocodedAddresses]
    );

    const reverseGeocodeCandidates = useMemo(() => {
        if (step !== DIALOG_STEPS.LIST) return [];
        if (!Array.isArray(results) || results.length === 0) return [];

        return results
            .map((r) => {
                const hasAddress = Boolean(buildAddressLabel(r));
                if (hasAddress) return null;
                const coords = extractLatLon(r);
                if (!coords) return null;
                return { webstoreId: r.webstoreId, ...coords };
            })
            .filter(Boolean);
    }, [results, step]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (reverseGeocodeCandidates.length === 0) return;

            await Promise.all(
                reverseGeocodeCandidates.map(async (c) => {
                    if (!c?.webstoreId || cancelled) return;
                    try {
                        const res = await geocodeService.getReverseGeoCode(c.lat, c.lon);
                        if (cancelled) return;
                        const label = res?.formattedAddress || res?.displayText;
                        if (!label) return;
                        setReverseGeocodedAddresses((prev) => {
                            if (prev[c.webstoreId]) return prev;
                            return { ...prev, [c.webstoreId]: label };
                        });
                    } catch {
                        // fall back to city/state in the list card
                    }
                })
            );
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [reverseGeocodeCandidates, geocodeService]);

    const redirectToConnect = useCallback(() => {
        const tokenService = TokenService();
        const baseUrl = process.env.REACT_APP_VENDORCONNECT_URL;
        const accessToken = tokenService?.getAccessToken?.();
        const refreshToken = tokenService?.getRefreshToken?.();
        const idToken = tokenService?.getIdToken?.();

        if (baseUrl && accessToken && refreshToken && idToken) {
            redirectToVendorConnectOrFallback(tokenService, navigate);
            return;
        }

        closeModal();
        redirectToVendorConnectOrFallback(tokenService, navigate);
    }, [closeModal, navigate]);

    const resetToList = () => {
        setStep(DIALOG_STEPS.LIST);
        setWebstoreToClaim(null);
        setCode('');
        setIsSending(false);
        setIsResending(false);
        setIsSubmitting(false);
    };

    const handleClaimFromList = (result) => {
        const claimable = toClaimableWebstore(result);
        if (onClaim) {
            onClaim(claimable);
            return;
        }
        setClaimableStore(claimable);
        setWebstoreToClaim(claimable);
        setCode('');
        setStep(DIALOG_STEPS.SEND_CODE);
    };

    const handleSendCode = async () => {
        if (!webstoreToClaim || isSending) return;
        setIsSending(true);
        try {
            const updated = await sendVerificationCode(webstoreToClaim);
            setWebstoreToClaim(updated);
            setStep(DIALOG_STEPS.VERIFY_CODE);
        } catch {
            // toast handled in hook
            setIsSending(false);
        }
    };

    const handleResendCode = async () => {
        if (!webstoreToClaim || isResending) return;
        setIsResending(true);
        try {
            const updated = await resendCode(webstoreToClaim);
            if (updated) {
                setWebstoreToClaim(updated);
            }
        } catch {
            // toast handled in hook
        } finally {
            setIsResending(false);
        }
    };

    const handleCompleteClaim = async () => {
        if (!webstoreToClaim || !code || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await completeClaim(webstoreToClaim, code, { redirectAfterClaim: false });
            setIsSubmitting(false);
            setStep(DIALOG_STEPS.CLAIM_SUCCESS);
        } catch {
            setIsSubmitting(false);
        }
    };

    return (
        <ClaimableWebstoresDialogStyled
            data-testid="claimable-webstores-dialog"
            data-signup-claim-modal="true"
        >
            {step === DIALOG_STEPS.LIST && (
                <ClaimableWebstoresDialogListStep
                    results={hydratedResults}
                    onClaim={handleClaimFromList}
                    onContinue={onContinue}
                    t={t}
                />
            )}
            {step === DIALOG_STEPS.SEND_CODE && webstoreToClaim && (
                <ClaimableWebstoresDialogSendCodeStep
                    webstoreToClaim={webstoreToClaim}
                    verificationEmail={verificationEmail}
                    supportEmail={supportEmail}
                    isSending={isSending}
                    onBack={resetToList}
                    onSendCode={handleSendCode}
                />
            )}
            {step === DIALOG_STEPS.VERIFY_CODE && webstoreToClaim && (
                <ClaimableWebstoresDialogVerifyCodeStep
                    webstoreToClaim={webstoreToClaim}
                    verificationEmail={verificationEmail}
                    supportEmail={supportEmail}
                    code={code}
                    isSubmitting={isSubmitting}
                    isResending={isResending}
                    onBack={resetToList}
                    onCodeChange={(e) => setCode(e.target.value.trim())}
                    onResendCode={handleResendCode}
                    onComplete={handleCompleteClaim}
                />
            )}
            {step === DIALOG_STEPS.CLAIM_SUCCESS && webstoreToClaim && (
                <ClaimableWebstoresDialogSuccessStep
                    webstoreToClaim={webstoreToClaim}
                    onContinue={redirectToConnect}
                    t={t}
                />
            )}
        </ClaimableWebstoresDialogStyled>
    );
};

export {
    ClaimableWebstoresDialog,
    DIALOG_STEPS,
    resolveCategoryName,
    resolveSubcategoryName,
    buildLocationLabel,
    buildAddressLabel,
    buildCategoryLabel,
    toClaimableWebstore,
};

export default ClaimableWebstoresDialog;
